/**
 * @author Aleksey I. Chelnokov <aleksey.chelnokov@auslogics.com>
 * @copyright Copyright (c) 2021 Auslogics Labs Pty Ltd (https://www.auslogics.com)
 */

import isObject from 'lodash/isObject';
import { useState } from 'react';

/**
 * Набор функций для работы с состоянием (get/set/del для mods/mix)
 * @param {Object} state объект состояния
 * @param {function (Object): void} setState сеттер нового состояния
 * @returns {Object}
 */
const createStateApi = (state, setState) => {
    const getVal = (name) => {
        return state[name];
    };

    const setVal = (name, value) => {
        if (getVal(name) !== value) {
            setState({ ...state, [name]: value });
        }
    };

    const delVal = (name) => {
        setState(prevState => {
            const nextState = { ...prevState };
            delete nextState[name];
            return nextState;
        });
    };

    return { delVal, getVal, setVal };
};

/**
 * Сборка массива CSS-классов для компонента
 * @param {React.Component} component компонент React
 * @param {string} className дополнительные CSS-классы
 * @param {boolean} shortName укоротить CSS-классы
 * @param {Object} mods модификаторы
 * @param {function (string): boolean | number | string} getMod геттер значения модификатора
 * @param {Object} mix миксы
 * @param {function (string): boolean | number | string} getMix геттер значения микса
 * @returns {string[]}
 */
const buildClassList = (component, className, shortName, mods, getMod, mix, getMix) => {
    const componentDisplayName = getComponentDisplayName(component);
    const classList = [componentDisplayName];

    if (className && (typeof className === 'string')) {
        classList.push(className);
    }

    // CSS-классы модификаторов
    const modClassList = bemClassList(mods, getMod, componentDisplayName, shortName);

    // CSS-классы миксов
    const mixClassList = bemClassList(mix, getMix, '', shortName);

    return [...classList, ...modClassList, ...mixClassList];
};

/**
 * Get display name for React component instance.
 * @param {Object} componentInstance
 * @return {string}
 */
const getComponentDisplayName = (componentInstance) => {
    const componentName = componentInstance.displayName ||
        componentInstance.constructor.displayName ||
        componentInstance.constructor.name ||
        'Component';

    return componentName.toLowerCase();
};

/**
 * Сборка массива CSS-классов в стиле BEM (для mods/mix)
 * @param {Object} values объект mods/mix
 * @param {function (string): boolean | number | string} getter функция получения значения mod/mix из values
 * @param {string} prefix префикс к названию CSS-класса
 * @returns {string[]}
 */
const bemClassList = (values, getter, prefix, shortName) => {
    const classList = [];

    if (prefix) prefix += '_';

    for (let name of Object.keys(values)) {
        const val = getter(name);

        if ((typeof val === 'boolean') && val) {
            classList.push(`${prefix}${name}`);
        } else if ((typeof val === 'number') || (typeof val === 'string')) {
            shortName ? name = '' : name += '_';

            classList.push(`${prefix}${name}${String(val).replace(/\s+/g, '-')}`);
        }
    }

    return classList;
};

/**
 * Проверка двух объектов на идентичность с точки зрения BEM.
 * Предназначается для проверки эквивалентности объектов модификаторов и миксов.
 * Проверка происходит лишь на самом верхнем уровне вложенности.
 * @param {Object} first первый объект
 * @param {Object} second второй объект
 * @returns {boolean} идентичность объектов с точки зрения BEM
 */
const compareBem = (first, second) => {
    if (!isObject(first) || !isObject(second)) {
        return false;
    }

    const firstKeys = new Set(Object.keys(first));
    const secondKeys = new Set(Object.keys(second));
    const allKeys = new Set([...firstKeys, ...secondKeys]);

    for (const key of allKeys) {
        if (!firstKeys.has(key) || !secondKeys.has(key) || (first[key] !== second[key])) {
            return false;
        }
    }

    return true;
};

/**
 * Hook для работы с mods/mix
 * @param {React.Component} component компонент React
 * @param {Object} options дополнительные CSS-классы, начальные mods и mix
 * @returns {Object} API для работы с mods/mix
 */
const useMods = (component, { className, mods: outerMods = {}, mix: outerMix = {} } = {}, shortName = false) => {
    // модификаторы
    const [mods, setMods] = useState(() => isObject(outerMods) ? outerMods : {});
    const { getVal: getMod, setVal: setMod, delVal: delMod } = createStateApi(mods, setMods);
    // если внешние модификаторы изменились, то обновить состояние
    if (!compareBem(mods, outerMods)) {
        setMods(outerMods);
    }

    // миксы
    const [mix, setMixes] = useState(() => isObject(outerMix) ? outerMix : {});
    const { getVal: getMix, setVal: setMix, delVal: delMix } = createStateApi(mix, setMixes);
    // если внешние миксы изменились, то обновить состояние
    if (!compareBem(mix, outerMix)) {
        setMixes(outerMix);
    }

    // итоговый массив CSS-классов
    const classList = buildClassList(component, className, shortName, mods, getMod, mix, getMix);

    return { className: classList.join(' '), delMix, delMod, getMix, getMod, setMix, setMod };
};

export default useMods;
